import {
  Component,
  OnInit,
  TemplateRef,
  Output,
  EventEmitter
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  Validators
} from '@angular/forms';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { AdminService } from '../../services/admin/admin.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { HelpersService } from '../../../shared/servicio/helpers/helpers.service';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.css']
})
export class CreateComponent implements OnInit {
  @Output() lista = new EventEmitter<boolean>();

  modalRef: BsModalRef;
  config = {
    class: 'modal-dialog-centered modal-app modal-app-lg',
    backdrop: true,
    ignoreBackdropClick: true
  };

  bannerForm: FormGroup;
  submitted: boolean = false;
  preloader: boolean = false;
  div_error: number = 0;
  div_text_error: string = '';

  // Archivos seleccionados
  selectedWebFile: File | null = null;
  selectedMobileFile: File | null = null;
  webFileName: string = '';
  mobileFileName: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private http: HttpClient,
    public admin: AdminService,
    private modalService: BsModalService,
    public _helper: HelpersService
  ) { }

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    this.bannerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      title: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(500)]],
      imageWeb: [null, [Validators.required]],
      imageMobile: [null, [Validators.required]],
      startDate: ['', [Validators.required]],
      endDate: ['', [Validators.required]],
      order: [1, [Validators.required, Validators.min(1)]],
      active: [true],
      bannerType: ['promotional', [Validators.required]],
      linkUrl: ['', [this.urlValidator]], // Validador personalizado para URL
      linkText: ['', [Validators.maxLength(50)]],
      displayDuration: [5, [Validators.min(1), Validators.max(30)]] // Cambiado a segundos, máximo 30
    }, { validators: this.dateValidator });
  }

  // Validador personalizado para URL
  urlValidator(control: FormControl) {
    if (!control.value || control.value.trim() === '') {
      return null; // URL vacía es válida (campo opcional)
    }

    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    const value = control.value.trim();

    // Si no empieza con http:// o https://, agregarlo
    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      const correctedValue = 'https://' + value;
      if (urlPattern.test(correctedValue)) {
        control.setValue(correctedValue);
        return null;
      }
    }

    return urlPattern.test(value) ? null : { invalidUrl: true };
  }

  // Validador personalizado para fechas
  dateValidator(group: FormGroup) {
    const startDate = group.get('startDate').value;
    const endDate = group.get('endDate').value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start >= end) {
        return { dateInvalid: true };
      }
    }
    return null;
  }

  get f() {
    return this.bannerForm.controls;
  }

  openModal(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, this.config);
    this.resetForm();
  }

  closeModal() {
    this.modalRef.hide();
    this.resetForm();
  }

  resetForm() {
    this.bannerForm.reset();
    this.initForm();
    this.submitted = false;
    this.preloader = false;
    this.div_error = 0;
    this.div_text_error = '';
    this.selectedWebFile = null;
    this.selectedMobileFile = null;
    this.webFileName = '';
    this.mobileFileName = '';
  }

  onWebFileSelected(event: any) {
    const file = event.target.files[0];
    console.log('Web file selected:', file);

    if (file) {
      if (this.isValidImageFile(file)) {
        if (file.size > 5 * 1024 * 1024) { // 5MB límite
          this.selectedWebFile = null;
          this.webFileName = '';
          this.bannerForm.patchValue({ imageWeb: null });
          this._helper.modalError('El archivo es demasiado grande. Máximo 5MB.');
          return;
        }

        this.selectedWebFile = file;
        this.webFileName = file.name;
        this.bannerForm.patchValue({ imageWeb: file });
        this.bannerForm.get('imageWeb').setErrors(null);

        // Limpiar errores generales si existían
        if (this.div_error === 1 && this.div_text_error.includes('imagen para la versión web')) {
          this.div_error = 0;
          this.div_text_error = '';
        }
      } else {
        this.selectedWebFile = null;
        this.webFileName = '';
        this.bannerForm.patchValue({ imageWeb: null });
        this._helper.modalError('Solo se permiten archivos JPG, PNG y JPEG');
      }
    } else {
      this.selectedWebFile = null;
      this.webFileName = '';
      this.bannerForm.patchValue({ imageWeb: null });
    }
  }

  onMobileFileSelected(event: any) {
    const file = event.target.files[0];
    console.log('Mobile file selected:', file);

    if (file) {
      if (this.isValidImageFile(file)) {
        if (file.size > 5 * 1024 * 1024) { // 5MB límite
          this.selectedMobileFile = null;
          this.mobileFileName = '';
          this.bannerForm.patchValue({ imageMobile: null });
          this._helper.modalError('El archivo es demasiado grande. Máximo 5MB.');
          return;
        }

        this.selectedMobileFile = file;
        this.mobileFileName = file.name;
        this.bannerForm.patchValue({ imageMobile: file });
        this.bannerForm.get('imageMobile').setErrors(null);

        // Limpiar errores generales si existían
        if (this.div_error === 1 && this.div_text_error.includes('imagen para la versión móvil')) {
          this.div_error = 0;
          this.div_text_error = '';
        }
      } else {
        this.selectedMobileFile = null;
        this.mobileFileName = '';
        this.bannerForm.patchValue({ imageMobile: null });
        this._helper.modalError('Solo se permiten archivos JPG, PNG y JPEG');
      }
    } else {
      this.selectedMobileFile = null;
      this.mobileFileName = '';
      this.bannerForm.patchValue({ imageMobile: null });
    }
  }

  private isValidImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const fileType = file.type.toLowerCase();

    // Verificar por tipo MIME
    if (validTypes.includes(fileType)) {
      return true;
    }

    // Verificar por extensión como fallback
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.jpg', '.jpeg', '.png'];

    return validExtensions.some(ext => fileName.endsWith(ext));
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  checkFormStatus() {
    console.log('=== FORM STATUS CHECK ===');
    console.log('Form valid:', this.bannerForm.valid);
    console.log('Form errors:', this.bannerForm.errors);
    console.log('Selected web file:', this.selectedWebFile);
    console.log('Selected mobile file:', this.selectedMobileFile);
    console.log('Submitted:', this.submitted);

    console.log('\n=== CONTROLS STATUS ===');
    Object.keys(this.bannerForm.controls).forEach(key => {
      const control = this.bannerForm.get(key);
      console.log(`${key}:`, {
        value: control.value,
        valid: control.valid,
        errors: control.errors,
        touched: control.touched
      });
    });
  }

  onSubmit() {
    console.log('=== ONSUBMIT CALLED ===');
    console.log('Form valid:', this.bannerForm.valid);
    console.log('Form value:', this.bannerForm.value);
    console.log('Form errors:', this.bannerForm.errors);
    console.log('Selected web file:', this.selectedWebFile);
    console.log('Selected mobile file:', this.selectedMobileFile);

    this.submitted = true;
    this.div_error = 0;
    this.div_text_error = '';

    // Validar que las fechas sean válidas (corregir acceso seguro)
    if (this.bannerForm.errors && this.bannerForm.errors['dateInvalid']) {
      this.div_error = 1;
      this.div_text_error = 'La fecha de inicio debe ser anterior a la fecha de fin';
      console.log('Error: Invalid dates');
      return;
    }

    // Validar archivos
    if (!this.selectedWebFile) {
      this.div_error = 1;
      this.div_text_error = 'Debe seleccionar una imagen para la versión web';
      console.log('Error: No web file selected');
      return;
    }

    if (!this.selectedMobileFile) {
      this.div_error = 1;
      this.div_text_error = 'Debe seleccionar una imagen para la versión móvil';
      console.log('Error: No mobile file selected');
      return;
    }

    // Validar formulario
    if (this.bannerForm.invalid) {
      this.div_error = 1;
      this.div_text_error = 'Por favor complete todos los campos requeridos correctamente';
      this.markFormGroupTouched(this.bannerForm);
      console.log('Error: Form invalid');
      return;
    }

    console.log('All validations passed, proceeding with submission...');

    this.preloader = true;

    const formData = this.bannerForm.value;
    const bannerData = {
      name: formData.name,
      title: formData.title,
      description: formData.description,
      imageWeb: this.selectedWebFile,
      imageMobile: this.selectedMobileFile,
      startDate: formData.startDate,
      endDate: formData.endDate,
      order: formData.order,
      active: formData.active,
      bannerType: formData.bannerType,
      linkUrl: formData.linkUrl || '',
      linkText: formData.linkText || '',
      displayDuration: formData.displayDuration || 5 // Valor en segundos
    };

    console.log('Sending banner data:', bannerData);

    this.admin.createBannerFormData(bannerData).subscribe({
      next: (event) => {
        console.log('Response event:', event);
        if (event.type === HttpEventType.UploadProgress) {
          const progress = Math.round(100 * event.loaded / event.total);
          console.log(`Upload progress: ${progress}%`);
        } else if (event.type === HttpEventType.Response) {
          console.log('Upload completed successfully');
          this.preloader = false;
          this._helper.modalSuccess('¡Banner creado exitosamente!');
          this.lista.emit(true);
          this.closeModal();
        }
      },
      error: (error) => {
        console.error('Error creating banner:', error);
        this.preloader = false;

        // Manejar errores específicos del backend
        if (error.error && typeof error.error === 'object') {
          const backendErrors = error.error;
          let errorMessage = 'Error al crear el banner:\n';

          if (backendErrors.link_url) {
            errorMessage += `• URL: ${backendErrors.link_url.join(', ')}\n`;
          }
          if (backendErrors.display_duration) {
            errorMessage += `• Duración: ${backendErrors.display_duration.join(', ')}\n`;
          }
          if (backendErrors.name) {
            errorMessage += `• Nombre: ${backendErrors.name.join(', ')}\n`;
          }
          if (backendErrors.title) {
            errorMessage += `• Título: ${backendErrors.title.join(', ')}\n`;
          }
          if (backendErrors.description) {
            errorMessage += `• Descripción: ${backendErrors.description.join(', ')}\n`;
          }

          this.div_error = 1;
          this.div_text_error = errorMessage;
        } else {
          this.div_error = 1;
          this.div_text_error = 'Error al crear el banner. Por favor, intente nuevamente.';
        }

        this._helper.error('Error al crear banner', error);
      }
    });
  }
}
